import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { processComparisonJob, CorrectionBreakdown } from "../../services/api";

const TOOL_COLOR = "#E53935";
const MAX_PDF_MB = 20;

const TYPE_LABELS: Record<string, string> = {
  text: "testo",
  missing_content: "contenuto mancante",
  instruction: "istruzione",
  layout: "layout",
  formatting: "formattazione",
  found_carryover: "carryover",
  other: "altro",
};

interface UploadBoxProps {
  label: string;
  accept: string;
  hint: string;
  file: File | null;
  color: string;
  onSelect: (f: File) => void;
}

const UploadBox: React.FC<UploadBoxProps> = ({
  label,
  accept,
  hint,
  file,
  color,
  onSelect,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;
    const acceptedExts = accept.split(",").map((s) => s.trim().toLowerCase());
    const ext = "." + dropped.name.split(".").pop()?.toLowerCase();
    if (acceptedExts.includes(ext)) {
      onSelect(dropped);
    }
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
      >
        {label}
      </Typography>
      <Box
        onClick={() => ref.current?.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: "2px dashed",
          borderColor: isDragging ? color : file ? color : "rgba(0,0,0,0.18)",
          borderRadius: "12px",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          cursor: "pointer",
          bgcolor: isDragging ? color + "18" : file ? color + "08" : "background.paper",
          transition: "all 0.2s",
          "&:hover": { borderColor: color, bgcolor: color + "08" },
          minHeight: 110,
          justifyContent: "center",
        }}
      >
        <UploadFileIcon
          sx={{ fontSize: "1.8rem", color: file ? color : isDragging ? color : "text.disabled" }}
        />
        {file ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color, wordBreak: "break-all" }}
            >
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(file.size / 1024).toFixed(1)} KB — clicca per cambiare
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {isDragging ? "Rilascia il file qui" : "Clicca o trascina qui"}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {hint}
            </Typography>
          </Box>
        )}
      </Box>
      <input
        ref={ref}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelect(f);
          e.target.value = "";
        }}
      />
    </Box>
  );
};

export const ComparatorTool: React.FC = () => {
  const [docxFile, setDocxFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [compareBold, setCompareBold] = useState(false);
  const [compareItalic, setCompareItalic] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultFilename, setResultFilename] = useState<string>("");
  const [correctionCount, setCorrectionCount] = useState<number | null>(null);
  const [correctionBreakdown, setCorrectionBreakdown] = useState<CorrectionBreakdown | null>(null);
  const [unanalysedPages, setUnanalysedPages] = useState<number[]>([]);
  const [progress, setProgress] = useState<{ pct: number; message: string } | null>(null);

  const handlePdfSelect = (f: File) => {
    if (f.size > MAX_PDF_MB * 1024 * 1024) {
      setError(`Il PDF è ${(f.size / (1024 * 1024)).toFixed(1)} MB — comprimilo sotto i ${MAX_PDF_MB} MB.`);
      return;
    }
    setError(null);
    setPdfFile(f);
    setResultBlob(null);
  };

  const handleProcess = async () => {
    if (!docxFile || !pdfFile) return;
    setLoading(true);
    setError(null);
    setResultBlob(null);
    setCorrectionCount(null);
    setCorrectionBreakdown(null);
    setUnanalysedPages([]);
    setProgress(null);

    try {
      const formData = new FormData();
      formData.append("docx_file", docxFile);
      formData.append("pdf_file", pdfFile);
      formData.append("compare_bold", String(compareBold));
      formData.append("compare_italic", String(compareItalic));

      const { blob, filename, corrections, breakdown, unanalysedPages } =
        await processComparisonJob(formData, (pct, message) => setProgress({ pct, message }));
      setResultBlob(blob);
      setResultFilename(filename);
      setCorrectionCount(corrections ?? null);
      setCorrectionBreakdown(breakdown ?? null);
      setUnanalysedPages(unanalysedPages);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Errore durante il confronto"
      );
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleDownload = () => {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = resultFilename || "annotato.pdf";
    a.click();
    URL.revokeObjectURL(url);
  };

  const canSubmit = !!docxFile && !!pdfFile && !loading;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 620,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* File uploads */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <UploadBox
          label="File Word (istruzioni evidenziate)"
          accept=".docx"
          hint=".docx"
          file={docxFile}
          color={TOOL_COLOR}
          onSelect={(f) => {
            setDocxFile(f);
            setResultBlob(null);
            setError(null);
          }}
        />
        <UploadBox
          label="PDF della grafica"
          accept=".pdf"
          hint={`.pdf — max ${MAX_PDF_MB} MB`}
          file={pdfFile}
          color={TOOL_COLOR}
          onSelect={handlePdfSelect}
        />
      </Box>

      {/* Options */}
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
        >
          Opzioni di confronto
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={compareItalic}
                onChange={(e) => setCompareItalic(e.target.checked)}
                size="small"
                sx={{ "&.Mui-checked": { color: TOOL_COLOR } }}
              />
            }
            label={<Typography variant="body2">Controlla corsivo</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={compareBold}
                onChange={(e) => setCompareBold(e.target.checked)}
                size="small"
                sx={{ "&.Mui-checked": { color: TOOL_COLOR } }}
              />
            }
            label={<Typography variant="body2">Controlla grassetto</Typography>}
          />
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: "10px" }}>
          {error}
        </Alert>
      )}

      {/* Success */}
      {resultBlob && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon />}
          sx={{ borderRadius: "10px" }}
        >
          {correctionCount === 0 ? (
            <>Confronto completato — <strong>nessun errore trovato</strong>.</>
          ) : correctionCount !== null ? (
            <>
              Confronto completato — trovate <strong>{correctionCount}</strong> correzioni
              {correctionBreakdown && Object.keys(correctionBreakdown).length > 0 && (
                <>
                  {": "}
                  {Object.entries(correctionBreakdown)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, count], idx, arr) => (
                      <React.Fragment key={type}>
                        <strong>{count}</strong> {TYPE_LABELS[type] ?? type}
                        {idx < arr.length - 1 ? ", " : ""}
                      </React.Fragment>
                    ))}
                </>
              )}
              .
            </>
          ) : (
            <>Confronto completato.</>
          )}
        </Alert>
      )}
      {resultBlob && unanalysedPages.length > 0 && (
        <Alert severity="warning" sx={{ borderRadius: "10px" }}>
          {unanalysedPages.length === 1 ? "La pagina" : "Le pagine"}{" "}
          <strong>{unanalysedPages.join(", ")}</strong>{" "}
          {unanalysedPages.length === 1 ? "non è stata verificata" : "non sono state verificate"}{" "}
          (modello AI non raggiungibile o nessun testo Word associato): controllale manualmente
          o rilancia il confronto.
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          fullWidth
          disabled={!canSubmit}
          onClick={handleProcess}
          sx={{
            borderRadius: "10px",
            py: 1.2,
            bgcolor: TOOL_COLOR,
            "&:hover": { bgcolor: "#B71C1C" },
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Confronta"}
        </Button>

        {resultBlob && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
            sx={{
              borderRadius: "10px",
              py: 1.2,
              borderColor: TOOL_COLOR,
              color: TOOL_COLOR,
              "&:hover": { borderColor: "#B71C1C", color: "#B71C1C" },
            }}
          >
            Scarica PDF annotato
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ textAlign: "center" }}>
          {progress && (
            <LinearProgress
              variant="determinate"
              value={Math.round(progress.pct * 100)}
              sx={{ mb: 1, borderRadius: "4px", "& .MuiLinearProgress-bar": { bgcolor: TOOL_COLOR } }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {progress?.message ? `${progress.message} — ` : ""}
            Analisi in corso, può richiedere diversi minuti a seconda del numero di pagine. Puoi lasciare la pagina aperta: il confronto prosegue sul server.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
